import { EditorMedia, isTikTokPhotoPost, keptBy, previewNotesOf } from 'utils/socialCampaign/postEditor'
import { FC, KeyboardEvent, useEffect, useId, useState } from 'react'
import { FeedCard, InstagramCard, PreviewCardProps, TikTokCard } from './PostPreviewCards.component'

import { EmptyContent } from 'components/common/EmptyContent'
import { FilterTab } from 'components/common/FilterTab'
import { NetworkMark } from './NetworkMark.component'
import { PreviewAccounts } from './usePreviewAccounts'
import { SocialNetwork } from 'models/socialCampaign'
import { SystemMessage } from 'components/common/SystemMessage'
import { VisibilityOutlined } from '@mui/icons-material'
import styles from './PostPreview.module.sass'
import { useTranslation } from 'react-i18next'

/**
 * The editor's preview column. The editor hands over what the post is (its networks, its one text, its visuals in
 * order) and who posts it; the preview draws each network's card and the notes of its media rules.
 */
export interface PostPreviewProps {
  /** The networks ticked for the post, in network order: one pill each, one preview at a time */
  networks: SocialNetwork[]
  /** The one text for every network, as typed */
  text: string
  /** The visuals in the order they go out */
  media: EditorMedia[]
  /** Reserved for an order: written once the content arrives, nothing to preview yet */
  waiting: boolean
  /** Who posts on each network (`usePreviewAccounts`); a network without one reads "Your <network> account" */
  accounts?: PreviewAccounts
  /** The post's subject: a TikTok photo post's title until ISSUE-3630 gives TikTok its own (prototype `p.title || p.subject`) */
  subject?: string
}

/**
 * One preview at a time behind the network pills (prototype v3 `previewHtml`): the notes say what the network leaves
 * out of the post (`previewNotesOf`), then its card shows only what it publishes (`keptBy`, the backend's rules).
 */
export const PostPreview: FC<PostPreviewProps> = ({ networks, text, media, waiting, accounts, subject }) => {
  const { t } = useTranslation(['social_campaign'])
  const id = `campaign-editor-preview-${useId().replace(/:/g, '')}`
  const [shown, setShown] = useState<SocialNetwork | null>(networks[0] ?? null)
  const network = shown && networks.includes(shown) ? shown : networks[0] ?? null
  useEffect(() => {
    if (network !== shown) setShown(network)
  }, [network, shown])

  const notes = network ? previewNotesOf(network, media) : []
  const card: PreviewCardProps | null = network && {
    network,
    account: accounts?.[network] ?? null,
    kept: keptBy(network, media),
    text: text || t('editor.preview.placeholder'),
  }

  // the pills are tabs: the arrows move between them (and never walk the posts)
  const onPillsKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!network || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return
    event.preventDefault()
    const at = networks.indexOf(network)
    const next = networks[(at + (event.key === 'ArrowRight' ? 1 : -1) + networks.length) % networks.length]
    setShown(next)
    document.getElementById(`${id}-${next}`)?.focus()
  }

  return (
    <>

      {waiting &&
        <EmptyContent
          titleComponent="h4"
          className={styles.empty}
          icon={<VisibilityOutlined sx={{ fontSize: '2.4rem' }} />}
          title={t('editor.preview.waiting_title')}
          description={t('editor.preview.waiting_body')}
        />
      }

      {!waiting && !network &&
        <EmptyContent
          titleComponent="h4"
          className={styles.empty}
          icon={<VisibilityOutlined sx={{ fontSize: '2.4rem' }} />}
          title={t('editor.preview.no_network_title')}
          description={t('editor.preview.no_network_body')}
        />
      }

      {!waiting && network &&
        <div className={styles.pills} role="tablist" aria-label={t('editor.preview.label')} onKeyDown={onPillsKey}>
          {networks.map((item) => (
            <FilterTab
              key={item}
              role="tab"
              id={`${id}-${item}`}
              ariaSelected={item === network}
              ariaControls={`${id}-card`}
              tabIndex={item === network ? 0 : -1}
              isActive={item === network}
              height="3.6rem"
              icon={<NetworkMark network={item} size={16} />}
              label={t(`network.${item}`)}
              onClick={() => setShown(item)}
            />
          ))}
        </div>
      }

      {!waiting && card &&
        <div className={styles.panel} role="tabpanel" id={`${id}-card`} aria-labelledby={`${id}-${card.network}`}>

          {notes.length > 0 &&
            <div className={styles.notes}>
              {notes.map((note) => (
                <SystemMessage
                  key={note.code}
                  variant={note.tone}
                  role="status"
                  isRawContent
                  message={t(`editor.preview.note.${note.code}`, { network: t(`network.${card.network}`), count: note.count, total: note.total })}
                />
              ))}
            </div>
          }

          {card.network === SocialNetwork.TIKTOK && <TikTokCard {...card} title={isTikTokPhotoPost(media) ? subject ?? '' : ''} />}

          {card.network === SocialNetwork.INSTAGRAM && <InstagramCard {...card} isReel={notes.some(({ code }) => code === 'reel')} />}

          {card.network !== SocialNetwork.TIKTOK && card.network !== SocialNetwork.INSTAGRAM && <FeedCard {...card} />}

        </div>
      }

    </>
  )
}
